import PropTypes from 'prop-types';
import { Box, Typography, Button, Avatar, Chip, Stack, CircularProgress } from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  CheckCircleRounded,
  AccessAlarmRounded,
  NotificationsActiveRounded,
} from '@mui/icons-material';
import StandardCard from '../../components/ui/StandardCard';
import WhatsAppBubble from './WhatsAppBubble';
import { brandColors } from '../../constants/brandColors';

const ICON_SX = { color: 'success.main', fontSize: 20, mt: '1px', flexShrink: 0 };
const BENEFITS = [
  { icon: <CheckCircleRounded sx={ICON_SX} />, text: 'Confirmação automática a cada novo agendamento.' },
  { icon: <AccessAlarmRounded sx={ICON_SX} />, text: 'Lembrete antes do horário, com botões Confirmar e Cancelar.' },
  { icon: <NotificationsActiveRounded sx={ICON_SX} />, text: 'Avisos no seu WhatsApp quando o cliente confirma ou cancela.' },
];

/**
 * WhatsAppHero — estado DESATIVADO da integração WhatsApp.
 * Apresenta valor + benefícios + exemplo de mensagem + CTA único de ativação.
 * Ativar persiste na hora (sem botão "Salvar" órfão).
 */
export default function WhatsAppHero({ sampleText, onActivate, activating }) {
  return (
    <StandardCard accentColor={brandColors.whatsapp.main} fillHeight={false} sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 3, md: 5 } }}>
        {/* Coluna texto */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Avatar
              variant="rounded"
              sx={{ bgcolor: 'rgba(37,211,102,0.12)', color: brandColors.whatsapp.main, width: 48, height: 48 }}
            >
              <WhatsAppIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>WhatsApp</Typography>
              <Chip
                label="Não ativado"
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', mt: 0.25 }}
              />
            </Box>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.25 }}>
            Confirme agendamentos e reduza faltas pelo WhatsApp
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520 }}>
            Ative para enviar confirmações e lembretes automáticos aos seus clientes — e receber
            avisos no seu próprio WhatsApp. Tudo configurável depois de ativar.
          </Typography>

          <Stack spacing={1.5} sx={{ mb: 3.5 }}>
            {BENEFITS.map((b) => (
              <Box key={b.text} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                {b.icon}
                <Typography variant="body2">{b.text}</Typography>
              </Box>
            ))}
          </Stack>

          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={activating ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />}
            onClick={onActivate}
            disabled={activating}
          >
            Ativar WhatsApp
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Você ajusta mensagens e lembretes depois. Pode desativar quando quiser.
          </Typography>
        </Box>

        {/* Coluna exemplo */}
        <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Exemplo de mensagem ao cliente
          </Typography>
          <WhatsAppBubble text={sampleText} />
        </Box>
      </Box>
    </StandardCard>
  );
}

WhatsAppHero.propTypes = {
  sampleText: PropTypes.string,
  onActivate: PropTypes.func.isRequired,
  activating: PropTypes.bool,
};
